import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  AmericanFlag,
  ChineseFlag,
  GermanFlag,
  ItalianFlag,
  SpanishFlag,
} from '@/components/Flags';
import { LanguageCodeType, EventDates } from '@/types';
import { RRule } from 'rrule';
import { formatInTimeZone } from 'date-fns-tz';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDateRange = (
  start: string | null | undefined,
  end: string | null | undefined,
): string | null => {
  // If both start and end dates are missing, return null
  if (!start && !end) return null;

  // Helper function to format a single date
  const formatDate = (dateString: string | null | undefined): string => {
    // If the date string is null or undefined, return 'Invalid date'
    if (!dateString) return 'Invalid date';

    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const [year, month, day] = dateString.split('-');
    const monthIndex = parseInt(month, 10) - 1;

    return `${months[monthIndex]} ${parseInt(day, 10)}, ${year}`;
  };

  // Format both start and end dates
  const startFormatted = formatDate(start);
  const endFormatted = formatDate(end);

  // If both start and end dates are provided
  if (start && end) {
    // If start and end dates are the same after formatting, return just one date
    if (startFormatted === endFormatted) {
      return startFormatted;
    }

    // If dates are different, return the range
    return `${startFormatted} - ${endFormatted}`;
  }

  // If only one date is provided, return the valid date (or 'Invalid date' if neither is valid)
  return startFormatted !== 'Invalid date' ? startFormatted : endFormatted;
};

export const formatTimeRange = (
  startDate: Date | string | number,
  endDate: Date | string | number,
): string => {
  if (!startDate || !endDate) {
    return 'Invalid date(s)';
  }

  const start = formatInTimeZone(startDate, 'America/New_York', 'h:mm a');
  const end = formatInTimeZone(endDate, 'America/New_York', 'h:mm a');

  return `${start} - ${end}`;
};

export const isExpired = (dates: EventDates[]): boolean => {
  if (!dates) return false;

  const currentDate = new Date();

  return dates.every((dateObj) => {
    if (!dateObj.frequency) return true;

    const { startDate, endDate, rrule } = dateObj.frequency;

    // If there's no rrule, just compare the endDate with current date
    if (!rrule) {
      return new Date(endDate) < currentDate;
    }

    // Parse the RRULE string and create new rule
    const parsedOptions = RRule.parseString(rrule);
    const rule = new RRule({
      ...parsedOptions,
      dtstart: new Date(startDate),
      until: new Date(endDate),
    });

    // Get all occurrences
    const occurrences = rule.all();

    // Check if all occurrences are in the past
    return occurrences.length > 0 && occurrences.every((date) => currentDate > date);
  });
};

export const formattedDate = (dateString: string): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateString));

interface Language {
  code: LanguageCodeType;
  name: string;
  flagComponent: React.ComponentType<any>;
}

export const supportedLanguages: Record<string, Language> = {
  en: { code: 'en', name: 'english', flagComponent: AmericanFlag },
  es: { code: 'es', name: 'spanish', flagComponent: SpanishFlag },
  de: { code: 'de', name: 'deutsch', flagComponent: GermanFlag },
  it: { code: 'it', name: 'italiano', flagComponent: ItalianFlag },
  zh: { code: 'zh', name: '中文 (繁體)', flagComponent: ChineseFlag },
};

export const getParentPageName = (name: string): string => {
  if (name === 'pressRelease') {
    return 'press';
  } else if (name === 'person') {
    return 'our-team';
  }
  return name.endsWith('s') ? `${name}es` : `${name}s`;
};

export const capitalizeWords = (str: string): string => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const generatePersonSlug = (
  firstName: string,
  lastName: string,
  nameOrder: string,
): string => {
  const formattedFirstName = firstName.trim().replace(/\s+/g, '-').toLowerCase();
  const formattedLastName = lastName.trim().replace(/\s+/g, '-').toLowerCase();

  const slug =
    nameOrder === 'firstLast'
      ? `${formattedFirstName}-${formattedLastName}`
      : `${formattedLastName}-${formattedFirstName}`;

  return slug;
};
